/* eslint-disable no-restricted-imports -- Chargement de Sentry */
import { ExtraErrorData } from '@sentry/integrations';
import * as Sentry from '@sentry/react';

import { isNumber, isObject, isString } from '~/src/common/utils/guards';

import config from '../config';
import type { Handler, Level, Logger } from '../types';

// Mapping de notre niveau de sévérité vers celui de Sentry
// -- Pour compatibilité avec @keplr/backend-logger
const Mapping = {
  debug: 'debug',
  info: 'info',
  warning: 'warning',
  error: 'error',
  critical: 'fatal',
} as const;

// Chargement et initialisation de Sentry
Sentry.init({
  enabled: config.enabled,
  dsn: config.sentryDsn,
  release: config.releaseName,
  environment: config.environment,
  ignoreErrors: [
    // Nos erreurs à nous, mais qu'on peut sereinement ignorer
    // Les erreurs liées à la config des navigateurs / crawlers
    /The operation is insecure/i, // Security policy avec scripts externes
    /Blocked a frame with origin/i, // Injection frame externe (extensions, test tool, etc..)
    /Loading chunk ui-gdpr-\w+-web failed/i, // Didomi blocké par un adblocker
    /Failed to read the 'localStorage' property from 'Window'/i, // Navigation privée et/ou config du browser
    /Non-Error promise rejection captured with value: Object Not Found Matching Id/i, // Outlook (https://github.com/getsentry/sentry-javascript/issues/3440)
    /ResizeObserver loop limit exceeded/i, // Problème peu fréquent à l'impact faible et dont la reproduction est difficile: https://stackoverflow.com/a/50387233/7477752
    // Les scripts externes, sur lesquels on n'a pas de contrôle
    /(reading|evaluating).*persist_condition'/i, // HotJar
    /Object\._canDisplayBanner \[as canDisplayBanner\]/i, // AppsFlyer
    /reading '[a-z0-9]{8}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{12}'/i, // AppsFlyer
    /Non-Error promise rejection captured with value: Could not find .+ in window/i, // GTM loaders
    // Erreurs non actionnables
    /<unknown module>/i,
    /IMUID is not defined/i,
    /Failed to execute 'removeChild' on 'Node'/i,
    /reading '(loaded|outputCurrentConfiguration)'/i,
  ],
  normalizeDepth: 6,
  attachStacktrace: true,
  integrations: defaultIntegrations => [...defaultIntegrations, new ExtraErrorData({ depth: 6 })],
});

// Pour remonter les logs non critiques sous forme de fil d'ariane
const getBreadcrumbsHandler = (level: Level): Handler => {
  return (message, data, opts) => {
    Sentry.addBreadcrumb({
      category: opts?.sentryBreadCrumbCategory ?? 'log',
      level: Mapping[level],
      message,
      data,
    });
  };
};

// Gestion des erreurs critiques
const handleException = (level: Level): Handler => {
  return (message, data = {}) => {
    Sentry.withScope(scope => {
      const fingerprintKeys = [level, message];
      const { accountId, originalError } = { ...data };
      const setTag = (key: string, val?: unknown) => {
        if (val != null) scope.setTag(key, String(val));
      };

      // Always save the account in tags, default to env if not available
      setTag('account_id', accountId ?? config.accountID);

      // Handle the original error, if it's defined
      if (isObject(originalError)) {
        // Extract values of interest from the original error
        const config = isObject(originalError.config) ? originalError.config : {};
        const request = isObject(originalError.request) ? originalError.request : {};
        const response = isObject(originalError.response) ? originalError.response : {};

        // Extract status code and URL
        const statusResponse = response.status;
        const statusRequest = request.status_code;
        const statusRData = isObject(response.data) ? response.data.status : null;
        const statusCode = statusResponse ?? statusRequest ?? statusRData;

        const url = request?.url ?? config?.url;

        // Update tags and fingerprints
        if (isString(url)) setTag('url', url);
        if (isString(statusCode) || isNumber(statusCode)) {
          setTag('status_code', statusCode);
          fingerprintKeys.push(String(statusCode));
        }
      }

      // Set error level and fingerprint keys
      scope.setLevel(Mapping[level]);
      scope.setFingerprint(fingerprintKeys);

      // Send error to sentry
      if (originalError instanceof Error) {
        const { originalError: _, ...rest } = data;
        scope.setContext('Log Message', { message, ...rest });
        Sentry.captureException(originalError);
      } else {
        scope.setContext('Log Message', data);
        Sentry.captureMessage(message);
      }
    });
  };
};

// Export du logger pour le frontend
export const browserLogger = {
  debug: getBreadcrumbsHandler('debug'),
  info: getBreadcrumbsHandler('info'),
  warning: handleException('warning'),
  error: handleException('error'),
  critical: handleException('critical'),
  setUser: Sentry.setUser.bind(Sentry),
  showReportDialog: Sentry.showReportDialog.bind(Sentry),
} satisfies Logger;
